import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Receipt, CreditCard, Clock, FileText } from 'lucide-react';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';

interface InvoicesProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const Invoices: React.FC<InvoicesProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const handleMobileMenuToggle = () => {
    const crmMenuItem = navigationItems.find(item => item.id === 'crm');
    if (crmMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmMenuItem.subItems, crmMenuItem.label);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Invoices | AgencyPlus</title>
        <meta name="description" content="Manage your invoices and payments" />
        <link rel="canonical" href="/crm/invoices" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          showSearch={false}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-foreground">Invoices</h1>
            <p className="text-muted-foreground">
              Track billings, payments, and financial status
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6 bg-emerald-500/15">
            <Receipt className="w-12 h-12 text-emerald-500" />
          </div>

          <h2 className="text-2xl font-bold mb-4 text-foreground">
            Coming Soon
          </h2>

          <p className="text-center max-w-md mb-8 text-muted-foreground">
            A complete financial management suite is coming soon to help you automate invoicing and collect payments faster.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-emerald-500/15">
                <FileText className="w-6 h-6 text-emerald-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Auto-Invoicing
              </h3>
              <p className="text-sm text-muted-foreground">
                Generate professional invoices automatically from your projects
              </p>
            </div>

            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-indigo-500/15">
                <CreditCard className="w-6 h-6 text-indigo-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Online Payments
              </h3>
              <p className="text-sm text-muted-foreground">
                Accept credit cards and bank transfers directly through the portal
              </p>
            </div>

            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-amber-500/15">
                <Clock className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Payment Tracking
              </h3>
              <p className="text-sm text-muted-foreground">
                Monitor aging invoices and send automatic reminders
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoices;
